import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GetTheApp } from "@/components/GetTheApp";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Clock, Calendar, User, Instagram, Share2 } from "lucide-react";
import ShareToInstagram from "@/components/ShareToInstagram";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [otherPosts, setOtherPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      setLoading(true); setNotFound(false); setPost(null);
      try {
        const { data } = await api.get(`/blog/posts/${slug}`);
        if (cancelled) return;
        setPost(data);
        // Load others for "more from the journal"
        try {
          const list = await api.get("/blog/posts");
          if (!cancelled) setOtherPosts(list.data.filter((p) => p.slug !== slug).slice(0, 2));
        } catch { /* ignore */ }
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 404) setNotFound(true);
          else setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Social crawlers (og:image, JSON-LD) need an absolute URL — resolve relative
  // /api/uploads/{id} covers against the backend origin.
  const _apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const coverAbs = post?.cover
    ? (/^https?:\/\//i.test(post.cover) ? post.cover : `${_apiBase}${post.cover}`)
    : undefined;

  useSEO({
    title: post ? `${post.title} · Better Through Boxing` : "Post · Better Through Boxing",
    description: post?.excerpt || "Boxing drills and coaching insights from Coach Chris.",
    path: `/blog/${slug}`,
    ogImage: coverAbs,
  });

  // Inject Article JSON-LD structured data for this post
  useEffect(() => {
    if (!post) return;
    const existing = document.getElementById("article-jsonld");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "article-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: coverAbs,
      author: { "@type": "Person", name: post.author, url: "https://www.instagram.com/flow_state_method" },
      publisher: {
        "@type": "Organization",
        name: "The Flow State Method LLC",
        alternateName: "Better Through Boxing",
        legalName: "The Flow State Method LLC",
        logo: { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_boxing-sessions-hub/artifacts/wt6d2dae_white_logo_transparent_background.png" },
      },
      datePublished: post.date,
      dateModified: post.updated_at || post.date,
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://betterthroughboxing.com/blog/${post.slug}` },
      keywords: (post.keywords || []).join(", "),
      articleSection: post.category,
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [post]);

  if (loading) {
    return (
      <div className="App">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center text-zinc-500 uppercase tracking-widest text-sm" data-testid="blog-post-loading">Loading…</main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="App">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="blog-post-not-found">
          <h1 className="font-display uppercase text-4xl mb-4">Post not found</h1>
          <Button onClick={() => navigate("/blog")} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm">Back to blog</Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // Coming-soon article — show locked teaser, blocked content, and a "back to journal" CTA.
  if (post.is_coming_soon || post.status === "coming_soon") {
    return (
      <div className="App relative">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="blog-post-coming-soon">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
            <Link to="/" className="hover:text-white">Better Through Boxing</Link>
            <span className="mx-2 text-zinc-700">/</span>
            <Link to="/blog" className="hover:text-white">Journal</Link>
            <span className="mx-2 text-zinc-700">/</span>
            <span className="text-amber-400">Coming Soon</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-amber-500 text-black text-[10px] uppercase tracking-[0.3em] font-bold px-3 py-1.5 mb-6">
            <Clock className="h-3 w-3" /> Coming Soon
          </div>
          <h1 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight leading-[0.95] mb-6">{post.title}</h1>
          <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-8">{post.excerpt}</p>
          {post.cover && (
            <figure className="mb-10 -mx-6 md:mx-0">
              <img src={post.cover} alt={post.title} className="w-full aspect-[16/9] object-cover grayscale border-y md:border border-amber-700/40" />
            </figure>
          )}
          <div className="border border-amber-700/40 bg-amber-950/10 p-6 md:p-8">
            <div className="text-amber-300 text-[10px] uppercase tracking-[0.3em] font-bold mb-2">Locked Article</div>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Coach Chris is still putting the finishing touches on this one. Subscribe and we&apos;ll send you a heads-up the moment it goes live.
            </p>
            <Link to="/blog">
              <Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="back-to-blog-from-locked">
                Back to journal <ArrowRight className="ml-2 h-3 w-3" />
              </Button>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const date = new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="App relative">
      <SiteHeader />
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="blog-post">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
          <Link to="/" className="hover:text-white">Better Through Boxing</Link>
          <span className="mx-2 text-zinc-700">/</span>
          <Link to="/blog" className="hover:text-white">Journal</Link>
          <span className="mx-2 text-zinc-700">/</span>
          <span className="text-[#FF331F]">{post.category}</span>
        </div>

        <div className="flex items-start justify-between gap-4 mb-6">
          <h1 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight leading-[0.95] flex-1">{post.title}</h1>
          <button
            onClick={() => setShareOpen(true)}
            className="shrink-0 mt-1 inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF331F] bg-[#0F0F0F] hover:bg-[#FF331F]/10 text-zinc-300 hover:text-white text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm transition-colors"
            data-testid="share-instagram-btn"
            title="Generate an Instagram card for this post"
          >
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-widest text-zinc-500 font-bold mb-10 pb-8 border-b border-[#27272A]" data-testid="post-meta">
          <span className="flex items-center gap-1.5"><User className="h-3 w-3 text-[#FF331F]" /> By {post.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-[#FF331F]" /> {date}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-3 w-3 text-[#FF331F]" /> {post.read_time}</span>
          <span className="flex items-center gap-1.5"><span className="h-1 w-1 rounded-full bg-[#FF331F]" /> {post.level}</span>
        </div>

        <figure className="mb-12 -mx-6 md:mx-0">
          <img src={post.cover} alt={post.title} className="w-full aspect-[16/9] object-cover border-y md:border border-[#27272A]" />
        </figure>

        <div className="prose-content space-y-5 text-zinc-200 text-[17px] leading-[1.7]">
          {(post.intro || []).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-14 space-y-14">
          {(post.sections || []).map((s, idx) => (
            <section key={`${s.num}-${idx}`} data-testid={`drill-${s.num}`} className="border-l-2 border-[#FF331F] pl-6 md:pl-8">
              <div className="flex items-start gap-5 mb-4 flex-wrap">
                <div className="font-display text-5xl md:text-6xl text-[#FF331F] leading-none">{s.num}</div>
                <div className="pt-1">
                  {s.category ? <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-1">{s.category}</div> : null}
                  <h2 className="font-display uppercase text-2xl md:text-3xl leading-tight">{s.title}</h2>
                </div>
              </div>
              <p className="text-zinc-200 leading-[1.7] text-[17px] whitespace-pre-line">{s.body}</p>
              {s.cue ? (
                <div className="mt-5 bg-[#FF331F]/8 border-l-2 border-[#FF331F] p-4 bg-[#0F0F0F]">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-1.5">Coach&apos;s Cue</div>
                  <p className="text-zinc-100 text-sm leading-relaxed italic">{s.cue}</p>
                </div>
              ) : null}
            </section>
          ))}
        </div>

        {post.outro && (post.outro.title || post.outro.body) ? (
          <div className="mt-20 border-2 border-[#FF331F] bg-gradient-to-br from-[#FF331F]/10 via-[#0F0F0F] to-[#0F0F0F] p-8 md:p-12" data-testid="post-cta">
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-none mb-4">{post.outro.title}</h2>
            <p className="text-zinc-200 leading-relaxed mb-8 whitespace-pre-line">{post.outro.body}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/book?package=btb_basics_program">
                <Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="cta-book-program">
                  Start the Basics Program <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/book?package=virtual_40">
                <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest rounded-sm bg-transparent" data-testid="cta-book-single">
                  Book a Single Session
                </Button>
              </Link>
            </div>
            <div className="mt-6 pt-6 border-t border-[#27272A] flex items-center justify-between flex-wrap gap-4 text-xs uppercase tracking-widest text-zinc-500">
              <span>© Better Through Boxing</span>
              <a href="https://www.instagram.com/flow_state_method" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-white">
                <Instagram className="h-3.5 w-3.5 text-[#FF331F]" /> @flow_state_method
              </a>
            </div>
          </div>
        ) : null}

        {otherPosts.length > 0 && (
          <div className="mt-20 pt-10 border-t border-[#27272A]">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-4">More from the journal</div>
            <div className="grid sm:grid-cols-2 gap-6">
              {otherPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group border border-[#27272A] bg-[#0F0F0F] p-5 hover:border-[#FF331F]/50 transition-colors">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{p.category}</div>
                  <h3 className="font-display uppercase text-lg leading-tight group-hover:text-[#FF331F] transition-colors">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Link to="/blog" className="mt-12 inline-flex items-center text-zinc-400 hover:text-white text-xs uppercase tracking-widest" data-testid="back-to-blog">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to blog
        </Link>

        <div className="mt-16">
          <GetTheApp variant="compact" />
        </div>
      </article>
      <ShareToInstagram open={shareOpen} onOpenChange={setShareOpen} post={post} />
      <SiteFooter />
    </div>
  );
}
